import Tooltip from "@components/Tooltip/Tooltip";
import type { Field as FieldType } from "@types/Field";
import { operations } from "@types/Field";
import type { RenderFieldProps } from "@types/RenderFieldProps";
import classNames from "classnames";
import { FormikErrors, FormikTouched, FormikValues } from "formik";
import React, { useEffect, useMemo } from "react";
import renderFieldType from "../ToolForm/RenderFieldType";
import FormFieldWithContext from "./FormFieldWithContext";
import dropdown from "@df/js/components/dropdown";

const getFieldClass = (
  field: FieldType,
  errors: FormikErrors<FormikValues>,
  touched: FormikTouched<FormikValues>,
) => {
  const fieldName = field.name as string;

  return classNames({
    "tool-form__form__field__input": true,
    "has-error": !!fieldName && !!errors[fieldName] && !!touched[fieldName],
    [`input-type-${field.type}`]: field.type,
    [`layout-${field.layout}`]: field.layout,
    [field.className]: field.className,
  });
};

const areConditionsMet = (
  field: FieldType,
  values: Record<string, any>,
): boolean => {
  if (!field.condition && !field.conditionsAnd) return true;

  if (field.condition) {
    if (Array.isArray(field.condition)) {
      return field.condition.some((cond) => {
        const { field: fieldName, value, operator = "===" } = cond;
        return operations[operator]?.(values[fieldName], value) ?? false;
      });
    }

    const { field: fieldName, value, operator = "===" } = field.condition;
    return operations[operator]?.(values[fieldName], value) ?? false;
  }

  if (field.conditionsAnd && Array.isArray(field.conditionsAnd)) {
    return field.conditionsAnd.every((cond) => {
      const { field: fieldName, value, operator = "===" } = cond;
      return operations[operator]?.(values[fieldName], value) ?? false;
    });
  }

  return false;
};

/**
 * Dynamically populates field options based on values from other form fields.
 * Useful for dependent dropdowns or fields that change options based on user selection.
 *
 * @param field - Field configuration with optional populate callback
 * @param values - Current form values
 * @returns Field with populated options or original field if no population needed
 */

const populateFieldOptions = (
  field: FieldType,
  values: Record<string, any>,
) => {
  if (!field.populate) return field;

  const populatedValue = values[field.populate.field];

  if (!populatedValue) return field;

  // Check if the field name matches any string ending with _number
  const regex = /_(\d+)$/;
  const match = field.name?.match(regex);

  if (match) {
    // Dynamically update the value based on index
    return {
      ...field,
      value: `${populatedValue}`,
    };
  } else {
    // No suffix, just return the field as-is
    return {
      ...field,
      options: field.populate.callback(populatedValue),
    };
  }
};

const renderTitleField = (field: FieldType, collapseItems?: () => void) => {
  const tooltipAttributes = field.error
    ? {
      "data-module": "tooltip",
      "data-content":
        "Tem campos em falta. Abra esta secção do formulário e preencha o campo assinalado.",
      "data-placement": "right",
      "data-theme": "error",
      "data-disable-overlay": true,
      "data-open": true,
    }
    : {};

  return (
    <div
      className="tool-form__heading"
      onClick={collapseItems}
      {...tooltipAttributes}
    >
      {field.titleLevel === 'h4'
        ? <h4 className={field.className}>{field.label}</h4>
        : <h5 className={field.className}>{field.label}</h5>
      }
      {field.collapsible && <div className="icon icon-expand-more" />}
    </div>
  );
};

const renderDefaultField = (
  field: FieldType,
  errors: FormikErrors<FormikValues>,
  touched: FormikTouched<FormikValues>,
  onFocus?: () => void,
  onBlur?: () => void,
): JSX.Element => {
  const fieldName = field.name as string;
  const showError = !!fieldName && !!errors[fieldName] && !!touched[fieldName];

  return (
    <div className={getFieldClass(field, errors, touched)}>
      {field.type !== "checkbox" && (
        <div className="tool-form__form__field__input__label">
          <label htmlFor={fieldName}>
            {field.label}
            {field.tooltip && (
              <Tooltip
                placement={field.tooltip.placement}
                title={field.tooltip.title}
              >
                {field.tooltip.content}
              </Tooltip>
            )}
          </label>
        </div>
      )}

      {renderFieldType({
        ...field,
        onFocus,
        onBlur,
      })}

      {showError && (
        <div className="tool-form__form__field__input__error">
          {errors[fieldName] as React.ReactNode}
        </div>
      )}
    </div>
  );
};

const RenderFieldWithContext: React.FC<RenderFieldProps> = ({
                                                              field,
                                                              errors,
                                                              values,
                                                              collapseItems,
                                                              onFocus,
                                                              onBlur,
                                                              touched,
                                                              hasSubmitted = false,
                                                            }) => {
  const isVisible = useMemo(() => areConditionsMet(field, values), [field, values]);

  const populatedField = useMemo(
    () => populateFieldOptions(field, values),
    [field, values],
  );

  useEffect(() => {
    if (!field.populate || !field.name) return;

    try {
      dropdown.refresh(field.name);
    } catch {
      // ignore
    }

    const parentValue = values[field.populate.field];
    if (!parentValue) return;

    const newOptions = field.populate.callback(parentValue);
    const currentValue = values[field.name];
    const isCurrentValueValid = newOptions.some(
      (opt) => String(opt.value) === String(currentValue),
    );

    if (!isCurrentValueValid && newOptions.length > 0 && window.globalSetFieldValue) {
      window.globalSetFieldValue(field.name, newOptions[0].value);
    }
  }, [field.populate, field.name, values]);

  if (!isVisible) {
    return null;
  }

  switch (populatedField.type) {
    case "title":
      return renderTitleField(populatedField, collapseItems);

    case "heading":
      return (
        <div className="tool-form__form__field__heading">
          <h6 className="font-semibold">{populatedField.label}</h6>
          {populatedField.tooltip && (
            <div className="tool-form__form__field__heading__tooltip">
              <Tooltip
                placement={populatedField.tooltip.placement}
                title={populatedField.tooltip.title}
              >
                {populatedField.tooltip.content}
              </Tooltip>
            </div>
          )}
        </div>
      );

    case "group":
      return (
        <>
          {populatedField.group_fields?.map((item, index) => (
            <FormFieldWithContext
              key={item.name || index}
              touched={touched}
              field={item}
              errors={errors}
              values={values}
              index={index}
              hasSubmitted={hasSubmitted}
            />
          ))}
        </>
      );

    default:
      return renderDefaultField(
        populatedField,
        errors,
        touched,
        onFocus,
        onBlur,
      );
  }
};

export default RenderFieldWithContext;
